import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, ArrowRight, Check, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"];
const HIRING_VOLUMES = ["Under 10 / year", "10-50 / year", "50-200 / year", "200+ / year"];

export default function EnterpriseInquiry() {
    const [form, setForm] = useState({
        company: "", contact_name: "", work_email: "", phone: "",
        company_size: "", hiring_volume: "", current_ats: "",
        expected_seats: "", message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const submit = async (e) => {
        e.preventDefault();
        if (!form.company || !form.contact_name || !form.work_email) {
            toast.error("Company, contact name and work email are required.");
            return;
        }
        try {
            setSubmitting(true);
            await api.post("/leads/enterprise", {
                ...form,
                expected_seats: form.expected_seats ? Number(form.expected_seats) : undefined,
            });
            setSent(true);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (sent) {
        return (
            <div className="min-h-screen flex items-center justify-center px-6 py-16">
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg text-center card-soft p-10">
                    <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-500 text-white flex items-center justify-center mb-4"><Check size={24} /></div>
                    <h1 className="font-heading font-semibold text-2xl text-foreground">Thanks — we'll be in touch.</h1>
                    <p className="mt-2 text-sm text-muted-foreground">Our team reviews Enterprise inquiries within one business day. Keep an eye on <b className="text-foreground">{form.work_email}</b>.</p>
                    <Link to="/" className="btn-outline mt-6 inline-flex text-sm">Back home</Link>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto px-6 py-12 md:py-20">
            <div className="mb-8">
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-violet-600 font-semibold">
                    <Building2 size={13} /> Simonara Enterprise
                </div>
                <h1 className="mt-2 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground">
                    Talk to sales
                </h1>
                <p className="mt-3 text-sm text-muted-foreground max-w-lg">
                    Custom seats, SSO/SAML, dedicated onboarding, priority support, and volume-based pricing for teams hiring at scale. Fill this out and someone will reply within one business day.
                </p>
            </div>
            <form onSubmit={submit} data-testid="enterprise-form" className="space-y-4 card-soft p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="text-xs font-medium text-foreground">Company *</label>
                        <Input required data-testid="ent-company" value={form.company} onChange={update("company")} className="mt-1" /></div>
                    <div><label className="text-xs font-medium text-foreground">Your name *</label>
                        <Input required data-testid="ent-name" value={form.contact_name} onChange={update("contact_name")} className="mt-1" /></div>
                    <div><label className="text-xs font-medium text-foreground">Work email *</label>
                        <Input required type="email" data-testid="ent-email" value={form.work_email} onChange={update("work_email")} className="mt-1" /></div>
                    <div><label className="text-xs font-medium text-foreground">Phone (optional)</label>
                        <Input data-testid="ent-phone" value={form.phone} onChange={update("phone")} className="mt-1" /></div>
                    <div><label className="text-xs font-medium text-foreground">Company size</label>
                        <select value={form.company_size} onChange={update("company_size")} data-testid="ent-size" className="mt-1 w-full h-10 px-3 rounded-md border border-border bg-background text-sm">
                            <option value="">Select…</option>{COMPANY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select></div>
                    <div><label className="text-xs font-medium text-foreground">Hiring volume</label>
                        <select value={form.hiring_volume} onChange={update("hiring_volume")} data-testid="ent-volume" className="mt-1 w-full h-10 px-3 rounded-md border border-border bg-background text-sm">
                            <option value="">Select…</option>{HIRING_VOLUMES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select></div>
                    <div><label className="text-xs font-medium text-foreground">Current ATS (if any)</label>
                        <Input data-testid="ent-ats" value={form.current_ats} onChange={update("current_ats")} placeholder="Greenhouse, Lever, Workable, …" className="mt-1" /></div>
                    <div><label className="text-xs font-medium text-foreground">Expected seats</label>
                        <Input type="number" min="0" max="10000" data-testid="ent-seats" value={form.expected_seats} onChange={update("expected_seats")} className="mt-1" /></div>
                </div>
                <div><label className="text-xs font-medium text-foreground">What are you trying to solve?</label>
                    <Textarea data-testid="ent-message" value={form.message} onChange={update("message")} rows={4} className="mt-1" placeholder="A few sentences on your hiring goals, roles you're filling, and any must-haves." /></div>
                <button type="submit" disabled={submitting} data-testid="ent-submit" className="btn-primary text-sm inline-flex items-center disabled:opacity-60">
                    {submitting ? <><Loader2 size={13} className="animate-spin mr-2" />Sending…</> : <>Send inquiry <ArrowRight size={13} className="ml-2" /></>}
                </button>
            </form>
        </div>
    );
}
